/**
 * DemoTour.jsx - Interactive walkthrough overlay.
 *
 * Walks the user through every major feature of the app across multiple
 * pages. Each step can optionally highlight a specific DOM element via a
 * CSS selector (data-tour attribute), navigate to a different page, and
 * fire a custom event (e.g. to auto-fill the Compare slots for demo).
 *
 * The tour card is draggable so it doesn't permanently block content.
 * Position is computed fresh on each step but can be moved by the user;
 * the drag offset is tracked via refs to avoid re-renders during drag.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Each step defines the page to visit, the element to spotlight, and the
// title/body text shown in the tour card.
const STEPS = [
  {
    page: '/dashboard', target: null, dim: true,
    title: 'Welcome to PlayerIQ',
    body: 'PlayerIQ is an AI-powered NFL fantasy analytics platform. This tour walks through every feature — live projections, analytics, player comparisons, and model accuracy.',
  },
  {
    page: '/dashboard', target: '[data-tour="navbar-badges"]',
    title: 'Live Model Status',
    body: 'The navbar shows the current prediction week and the model\'s MAE — average prediction error in fantasy points. Click the MAE badge to see a full accuracy breakdown by position.',
  },
  {
    page: '/dashboard', target: '[data-tour="summary-cards"]',
    title: 'Weekly Snapshot',
    body: 'At a glance: total players projected this week, how many are low-risk starts, and the top projected player with their score.',
  },
  {
    page: '/dashboard', target: '[data-tour="accuracy-banner"]',
    title: 'Accuracy Banner',
    body: 'Real MAE and R² scores by position, calculated on held-out test data and shown right on the projections page — so you always know how much to trust the numbers.',
  },
  {
    page: '/dashboard', target: '[data-tour="trending"]',
    title: 'Trending This Week',
    body: 'The top 3 projected players for the current week, each showing their confidence interval floor and ceiling.',
  },
  {
    page: '/dashboard', target: '[data-tour="pos-filters"]',
    title: 'Position Filters',
    body: 'Filter the projection table instantly by position — QB, RB, WR, TE, K, or DST.',
  },
  {
    page: '/dashboard', target: '[data-tour="player-table"]',
    title: 'Projection Table',
    body: 'Every player sorted by projected points. Each row shows the ML prediction, floor and ceiling from the 80% confidence interval, and a risk label based on CI width. Click any row to open a detail panel.',
  },
  {
    page: '/analytics', target: '[data-tour="season-selector"]',
    title: 'Analytics — 11 Seasons of Data',
    body: 'The Analytics page covers historical NFL data from 2015–2025. Switch between seasons using the selector shown here.',
  },
  {
    page: '/analytics', target: '[data-tour="analytics-cards"]',
    title: 'Season Summary',
    body: 'Total players tracked, the season\'s top scorer with total fantasy points, and the full historical data range in the system.',
  },
  {
    page: '/analytics', target: '[data-tour="leaderboard-table"]',
    title: 'Season Leaderboard',
    body: 'All players ranked by total fantasy points. Filterable by position, searchable by name. Click any row to jump to that player\'s trend chart.',
  },
  {
    page: '/analytics', target: '[data-tour="trends-tab"]', event: 'demo:showTrends',
    title: 'Player Trends Tab',
    body: 'Click the Player Trends tab — or click any leaderboard row — to see week-by-week charts overlaid with the latest ML projection point.',
  },
  {
    page: '/analytics', target: null, dim: false,
    title: 'Player Trend Chart',
    body: 'Week-by-week fantasy points across multiple seasons. The blue dot at the right edge is the current ML projection — showing historical context for the model\'s prediction.',
  },
  {
    page: '/compare', target: null, dim: false, event: 'demo:compareAutoFill',
    title: 'Player Comparison',
    body: 'Head-to-head comparison tool — loading the top two projected players now so you can see it in action.',
  },
  {
    page: '/compare', target: '[data-tour="compare-slots"]',
    title: 'Start / Sit Tool',
    body: 'Each slot shows the player\'s projected points, risk level, floor and ceiling, and a bar chart of recent weekly performance. Exportable to CSV.',
  },
  {
    page: '/team-builder', target: null, dim: true, event: 'demo:teamBuilderAutoFill',
    title: 'Fantasy Team Builder',
    body: 'Build a full 15-player roster using the latest projections. Click any roster slot to activate it, then pick a player from the pool — filling a complete lineup now to show you how it works.',
  },
  {
    page: '/team-builder', target: '[data-tour="team-summary"]',
    title: 'Team Projected Score',
    body: 'Live team totals update as you build. See your projected total points, floor (worst case), ceiling (best case), and how many starters you\'ve filled out of 9.',
  },
  {
    page: '/team-builder', target: '[data-tour="team-roster"]',
    title: 'Roster Slots',
    body: 'Standard fantasy lineup: QB, RB×2, WR×2, TE, FLEX (RB/WR/TE), K, DST, and 6 bench spots. Click a slot to activate it, then pick from the pool. Each filled slot shows the projection and risk rating.',
  },
  {
    page: '/team-builder', target: '[data-tour="player-pool"]',
    title: 'Player Pool',
    body: 'All available players sorted by projected points — with floor–ceiling range and risk label. Filter by position, search by name, and ineligible players gray out automatically when a slot is active.',
  },
  {
    page: '/accuracy', target: null, dim: false,
    title: 'Model Accuracy Deep Dive',
    body: 'Full breakdown of model performance — MAE, RMSE, and R² per position and week, plus actual vs predicted scatter plots across the full season.',
  },
  {
    page: null, target: null, dim: true,
    title: "That's PlayerIQ",
    body: 'Built for CSC 400 Senior Capstone · 11 seasons of NFL data · XGBoost + LightGBM ensemble · ~70% directional accuracy · FastAPI + PostgreSQL · Deployed on AWS EC2.',
  },
];

// Tour card dimensions — used to clamp position so it stays on screen
const CARD_W = 380;
const CARD_H = 320;
const PAD    = 16;

function clampPos(pos) {
  return {
    left: Math.max(PAD, Math.min(pos.left, window.innerWidth  - CARD_W - PAD)),
    top:  Math.max(PAD, Math.min(pos.top,  window.innerHeight - CARD_H - PAD)),
  };
}

/**
 * Given the bounding rect of the spotlight element, compute where to place
 * the tour card so it doesn't overlap the highlighted area.
 * Tries below, above, right, left in order, then falls to the furthest corner.
 */
function computeInitialPos(spotRect) {
  const W = window.innerWidth;
  const H = window.innerHeight;
  const GAP = PAD + 8;
  const clampL = l => Math.max(PAD, Math.min(l, W - CARD_W - PAD));
  const clampT = t => Math.max(PAD, Math.min(t, H - CARD_H - PAD));

  if (!spotRect) {
    return { top: clampT(H / 2 - CARD_H / 2), left: clampL(W / 2 - CARD_W / 2) };
  }

  const sT = spotRect.top    - 8;
  const sB = spotRect.top    + spotRect.height + 8;
  const sL = spotRect.left   - 8;
  const sR = spotRect.left   + spotRect.width  + 8;
  const cX = spotRect.left   + spotRect.width  / 2;
  const cY = spotRect.top    + spotRect.height / 2;

  if (H - sB >= CARD_H + GAP) return { top: sB + GAP,            left: clampL(cX - CARD_W / 2) };
  if (sT      >= CARD_H + GAP) return { top: sT - CARD_H - GAP,  left: clampL(cX - CARD_W / 2) };
  if (W - sR  >= CARD_W + GAP) return { top: clampT(cY - CARD_H / 2), left: sR + GAP };
  if (sL      >= CARD_W + GAP) return { top: clampT(cY - CARD_H / 2), left: sL - CARD_W - GAP };
  // No clear side — place in the corner furthest from the spotlight
  return { top: cY > H / 2 ? PAD : H - CARD_H - PAD, left: cX > W / 2 ? PAD : W - CARD_W - PAD };
}

const DemoTour = ({ onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [step,     setStep]     = useState(0);
  const [spotRect, setSpotRect] = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [cardPos,  setCardPos]  = useState(() => computeInitialPos(null));

  const searchTimer = useRef(null);
  const isDragging  = useRef(false);
  const dragOffset  = useRef({ x: 0, y: 0 });

  const current = STEPS[step];

  // Navigate to the step's page if we're not already there
  useEffect(() => {
    if (current.page && location.pathname !== current.page) {
      setLoading(true);
      setSpotRect(null);
      navigate(current.page);
    }
  }, [step]); // eslint-disable-line

  // Once on the right page, find the target element and position the spotlight
  useEffect(() => {
    if (current.page && location.pathname !== current.page) return;

    clearTimeout(searchTimer.current);

    if (!current.target) {
      searchTimer.current = setTimeout(() => {
        setSpotRect(null);
        setLoading(false);
        if (!isDragging.current) setCardPos(clampPos(computeInitialPos(null)));
        if (current.event) window.dispatchEvent(new CustomEvent(current.event));
      }, loading ? 400 : 100);
      return;
    }

    searchTimer.current = setTimeout(() => {
      const el = document.querySelector(current.target);
      if (!el) { setLoading(false); return; }
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        const r = el.getBoundingClientRect();
        const rect = { top: r.top, left: r.left, width: r.width, height: r.height };
        setSpotRect(rect);
        // Only reposition the card if the user hasn't manually moved it this step
        if (!isDragging.current) setCardPos(clampPos(computeInitialPos(rect)));
        setLoading(false);
        if (current.event) setTimeout(() => window.dispatchEvent(new CustomEvent(current.event)), 200);
      }, 300);
    }, loading ? 500 : 80);

    return () => clearTimeout(searchTimer.current);
  }, [step, location.pathname]); // eslint-disable-line

  // Keep the spotlight in sync as the user scrolls or resizes
  useEffect(() => {
    if (!current.target) return;
    const sync = () => {
      const el = document.querySelector(current.target);
      if (el) {
        const r = el.getBoundingClientRect();
        setSpotRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }
    };
    window.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    return () => { window.removeEventListener('scroll', sync); window.removeEventListener('resize', sync); };
  }, [current.target]);

  // --- Navigation ---

  const advance = (dir) => {
    const next = step + dir;
    if (next < 0) return;
    if (next >= STEPS.length) { onClose(); return; }

    const nextStep  = STEPS[next];
    const crossPage = nextStep.page && nextStep.page !== location.pathname;

    if (crossPage) {
      setLoading(true);
      setSpotRect(null);
    }
    setStep(next);
  };

  // --- Drag handling ---

  const handleDragStart = (e) => {
    // Don't start a drag if the user clicked a button inside the handle area
    if (e.target.closest('button')) return;
    e.preventDefault();

    isDragging.current = true;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragOffset.current = { x: clientX - cardPos.left, y: clientY - cardPos.top };

    const onMove = (ev) => {
      if (!isDragging.current) return;
      const cx = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const cy = ev.touches ? ev.touches[0].clientY : ev.clientY;
      setCardPos(clampPos({
        left: cx - dragOffset.current.x,
        top:  cy - dragOffset.current.y,
      }));
    };

    const onUp = () => {
      isDragging.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',   onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend',  onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend',  onUp);
  };

  // Show a dark overlay when there's no specific element to spotlight
  const showDim = loading || (!spotRect && current.dim !== false);

  return (
    <>
      {/* Dark overlay behind everything */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          zIndex: 9998,
          background: 'rgba(0,0,0,0.70)',
          opacity: showDim ? 1 : 0,
          transition: 'opacity 0.4s ease',
        }}
      />

      {/* Spotlight cutout — a box shadow that masks everything except the target element */}
      <div
        style={{
          position: 'fixed',
          top:    spotRect ? spotRect.top    - 8 : window.innerHeight / 2 - 4,
          left:   spotRect ? spotRect.left   - 8 : window.innerWidth  / 2 - 4,
          width:  spotRect ? spotRect.width  + 16 : 8,
          height: spotRect ? spotRect.height + 16 : 8,
          borderRadius: 10,
          boxShadow: spotRect ? '0 0 0 9999px rgba(0,0,0,0.72)' : 'none',
          border: spotRect ? '2px solid rgba(77,124,255,0.80)' : 'none',
          opacity: spotRect && !loading ? 1 : 0,
          pointerEvents: 'none',
          zIndex: 9998,
          transition: 'top 0.4s ease, left 0.4s ease, width 0.4s ease, height 0.4s ease, opacity 0.3s ease',
        }}
      />

      {/* Tour card — no CSS transition because position is user-controlled via drag */}
      <div
        style={{ position: 'fixed', top: cardPos.top, left: cardPos.left, width: CARD_W, zIndex: 9999 }}
        className="pointer-events-auto"
      >
        <div className="bg-[#0b1120] border border-[#4d7cff]/40 rounded-2xl shadow-[0_12px_48px_rgba(0,0,0,0.9)] overflow-hidden">

          {/* Step progress bar across the top */}
          <div className="flex h-[3px]">
            {STEPS.map((_, i) => (
              <div key={i} style={{ flex: 1, marginRight: i < STEPS.length - 1 ? 1 : 0 }}
                className={`transition-all duration-500 ${i <= step ? 'bg-[#4d7cff]' : 'bg-white/10'}`}
              />
            ))}
          </div>

          {/* Drag handle — the entire header row is draggable */}
          <div
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
            className="flex items-center justify-between px-5 pt-4 pb-0 cursor-grab active:cursor-grabbing select-none"
          >
            <span className="text-[10px] text-white/30 uppercase tracking-widest font-medium">
              {loading ? (
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 border border-[#4d7cff]/50 border-t-[#4d7cff] rounded-full animate-spin inline-block"/>
                  Loading...
                </span>
              ) : `${step + 1} / ${STEPS.length}`}
            </span>
            <div className="flex items-center gap-2">
              {/* Grip dots visual indicator */}
              <div className="flex flex-col gap-[3px] opacity-20">
                <div className="flex gap-[3px]">{[0,1,2].map(i=><div key={i} className="w-1 h-1 rounded-full bg-white"/>)}</div>
                <div className="flex gap-[3px]">{[0,1,2].map(i=><div key={i} className="w-1 h-1 rounded-full bg-white"/>)}</div>
              </div>
              <button
                onMouseDown={e => e.stopPropagation()}
                onClick={onClose}
                className="text-white/20 hover:text-white/50 transition-colors text-xs px-2 py-0.5"
              >
                ✕ Skip
              </button>
            </div>
          </div>

          <div className="p-5 pt-3">
            <h3 className="text-white font-bold text-[15px] mb-2">{current.title}</h3>
            <p className="text-white/55 text-sm leading-relaxed mb-5">{current.body}</p>

            <div className="flex items-center justify-between">
              {/* Dot pagination indicators */}
              <div className="flex gap-1 flex-wrap max-w-[160px]">
                {STEPS.map((_, i) => (
                  <div key={i}
                    className={`rounded-full transition-all duration-300 ${i === step ? 'w-4 h-1.5 bg-[#4d7cff]' : 'w-1.5 h-1.5 bg-white/15'}`}
                  />
                ))}
              </div>
              <div className="flex gap-2">
                {step > 0 && (
                  <button onClick={() => advance(-1)}
                    className="px-4 py-2 rounded-xl bg-white/[0.06] hover:bg-white/10 text-white/60 text-sm font-medium transition-colors">
                    ← Back
                  </button>
                )}
                <button onClick={() => advance(1)}
                  className="px-5 py-2 rounded-xl bg-[#4d7cff] hover:bg-[#3d6cef] text-white text-sm font-bold transition-all shadow-[0_4px_12px_rgba(77,124,255,0.4)]">
                  {step === STEPS.length - 1 ? 'Finish ✓' : 'Next →'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DemoTour;
